(() => {
  'use strict';

  if (customElements.get('cart-recommendations')) return;

  class CartRecommendations extends HTMLElement {
    constructor() {
      super();
      this.version = 0;
      this.onCartChange = this.onCartChange.bind(this);
    }

    connectedCallback() {
      this.block = this.closest('.cart-block');
      this.products = this.querySelector('[data-cart-recommendations-products]');
      this.automatic = this.dataset.automatic === 'true';
      this.limit = Math.min(10, Math.max(1, Number(this.dataset.limit) || 4));
      if (!this.block || !this.products) return;

      document.addEventListener('theme:cart:change', this.onCartChange);
      // Read the current cart on reconnection instead of reusing the server snapshot.
      if (this.initialized) {
        this.onCartChange();
        return;
      }
      this.initialized = true;

      const productIds = JSON.parse(this.dataset.cartProductIds || '[]');
      const items = productIds.map((productId) => ({product_id: productId}));
      this.controller = new AbortController();
      this.update({items}, ++this.version, this.controller.signal);
    }

    disconnectedCallback() {
      document.removeEventListener('theme:cart:change', this.onCartChange);
      clearTimeout(this.timer);
      this.controller?.abort();
      this.version++;
    }

    onCartChange() {
      // Invalidate immediately, including the debounce window.
      const version = ++this.version;
      clearTimeout(this.timer);
      this.controller?.abort();
      this.setVisible(false);
      this.timer = setTimeout(() => this.refresh(version), 80);
    }

    isCurrent(version) {
      return this.isConnected && version === this.version;
    }

    async refresh(version) {
      if (!this.isCurrent(version)) return;
      this.controller = new AbortController();
      const {signal} = this.controller;

      try {
        const response = await fetch(this.dataset.cartUrl, {signal, cache: 'no-store'});
        if (!response.ok) throw new Error('Unable to load cart');
        const cart = await response.json();
        if (!Array.isArray(cart.items)) throw new Error('Invalid cart response');
        if (this.isCurrent(version)) await this.update(cart, version, signal);
      } catch (error) {
        if (this.isCurrent(version)) this.setVisible(false);
      }
    }

    async update(cart, version, signal) {
      if (!this.isCurrent(version)) return;
      if (!cart.items.length) {
        if (this.automatic) this.products.replaceChildren();
        this.setVisible(false);
        return;
      }

      if (!this.automatic) {
        this.setVisible(Boolean(this.products.querySelector('quick-add-product')));
        return;
      }

      this.setVisible(false);
      try {
        const url = new URL(this.dataset.recommendationsUrl, window.location.origin);
        url.searchParams.set('section_id', 'api-cart-recommendations');
        url.searchParams.set('product_id', cart.items[0].product_id);
        url.searchParams.set('limit', this.limit);
        url.searchParams.set('intent', 'related');

        const response = await fetch(url.toString(), {signal});
        if (!response.ok) throw new Error('Unable to load recommendations');
        const html = await response.text();
        if (!this.isCurrent(version)) return;

        const content = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-recommendations-response]');
        if (!content) throw new Error('Invalid recommendations response');

        const cartProductIds = new Set(cart.items.map((item) => String(item.product_id)));
        let count = 0;
        content.querySelectorAll('quick-add-product').forEach((card) => {
          const productId = card.querySelector('[data-quick-add-holder]')?.getAttribute('data-quick-add-holder');
          if (!productId || cartProductIds.has(productId) || count >= this.limit) {
            card.remove();
          } else {
            count++;
          }
        });

        // Insert only cards; custom elements initialize the theme's quick-add controls.
        this.products.innerHTML = Array.from(content.querySelectorAll('quick-add-product'), (card) => card.outerHTML).join('');
        this.setVisible(count > 0);
      } catch (error) {
        if (this.isCurrent(version)) {
          this.products.replaceChildren();
          this.setVisible(false);
        }
      }
    }

    setVisible(visible) {
      this.block.classList.toggle('hidden', !visible);
    }
  }

  customElements.define('cart-recommendations', CartRecommendations);
})();
